/* eslint-disable no-useless-return */
/* eslint-disable no-console */
/* eslint-disable no-nested-ternary */
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import CustomLayout from "../../../Core/Layout/CustomLayout";
import StepProgress, { StepDescription } from "./StepProgress";
import CloseIcon from "../../../Common/Svgs/CloseIcon";
import Button from "../../../Core/Components/CustomButton";
import TripStep1 from "./TripStep1";
import TripStep2 from "./TripStep2";
import TripStep3 from "./TripStep3";
import TripStep4 from "./TripStep4";
import { resetTrips, updateTrips } from "../../../Redux/Slices/trips.slice";
import {
  HEADER_STEP_1,
  HEADER_STEP_2,
  HEADER_STEP_3,
  HEADER_STEP_4,
  HEADER_STEP_5,
} from "../Data/TripConstants";
import checkValidaton from "./validationForSteps";
import { createTrip, editTrip } from "../../../Redux/APIs/tripsAPI";
import TripStep1Modal from "./TripStep1Modal";
import CancelTripModal from "./CancelTripModal";
import TripStep5 from "./TripStep5";
import { useTenantPermission } from "../../../Core/hooks/usePermission";
import { TENANT_PERMISSIONS } from "../../Login/constants";

const handleResetState = ({ dispatch }) => {
  dispatch(resetTrips(["tripDetails"]));
  dispatch(resetTrips(["stops"]));
};

const handleToastMessage = ({ dispatch, toastMessage, type }) => {
  dispatch(
    updateTrips({
      key: "toastMessage",
      value: {
        ...toastMessage,
        visible: true,
        type,
      },
    }),
  );
};

const handleResponse = (
  response,
  dispatch,
  navigate,
  toastMessage,
  operationType,
) => {
  if (operationType === "create") {
    if (response.payload.message === "Trip created successfully") {
      navigate("/trips");
      handleResetState({ dispatch });
      handleToastMessage({ dispatch, toastMessage, type: "tripCreated" });
    }

    if (response.payload.response.data.message === "Trip already exists") {
      navigate("/trips");
      handleResetState({ dispatch });
      handleToastMessage({ dispatch, toastMessage, type: "tripExists" });
    }

    return;
  }
  if (operationType === "edit") {
    navigate("/trips");
    handleResetState({ dispatch });
    handleToastMessage({ dispatch, toastMessage, type: "tripUpdated" });

    return;
  }

  return;
};

const getSaveButtonText = ({
  currentStep,
  editTripDetails,
  hasTenantTrikePermission,
  hasTenantBatteryPermission,
  tripDetails,
}) => {
  // current tenant does not have trike permission
  if (!hasTenantTrikePermission) {
    // select driver step will be the final step for this tenant
    if (currentStep === 3) {
      // if editing
      if (editTripDetails?.flag) return "Save";
      // if creating a new trip
      return "Create Trip";
    }
    // for all other steps
    return "Next";
  }

  // current tenant does not have battery permission
  if (!hasTenantBatteryPermission) {
    // select trike step will be the final step for this tenant
    if (currentStep === 4) {
      // if editing
      if (editTripDetails?.flag) return "Save";
      // if creating a new trip
      return "Create Trip";
    }
    // for all other steps
    return "Next";
  }

  // if the selected trike is has an internal combustion engine then do not show select batteries step
  if (currentStep === 4 && tripDetails?.trikeType === "ICE") {
    // if editing
    if (editTripDetails?.flag) return "Save";
    // if creating a new trip
    return "Create Trip";
  }

  // select batteries step will be the final step for this tenant (because it has trike permission)
  if (currentStep === 5) {
    // if editing
    if (editTripDetails?.flag) return "Save";
    // if creating a new trip
    return "Create Trip";
  }
  // for all other steps
  return "Next";
};

const TripScreen = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const hasTenantTrikePermission = useTenantPermission([
    TENANT_PERMISSIONS.TRIKE_MODULE_ACCESS,
  ]);

  const hasTenantBatteryPermission = useTenantPermission([
    TENANT_PERMISSIONS.BATTERY_MODULE_ACCESS,
  ]);

  const {
    currentStep,
    stops,
    tripDetails,
    validationForSteps,
    toastMessage,
    editTripDetails,
    tripStep1Modal,
    cancelTripModal,
    isRouteOptimised,
    optimisedRouteDistance,
    optimisedRouteTime,
    calculatedRouteDistance,
    calculatedRouteTime,
  } = useSelector((state) => state.trips);

  const { allTrikes } = useSelector((state) => state.trikes);

  const [currentHeader, setCurrentHeader] = useState(HEADER_STEP_1);

  const changeStepHeader = (step) => {
    switch (step) {
      case 1:
        setCurrentHeader(HEADER_STEP_1);
        break;
      case 2:
        setCurrentHeader(HEADER_STEP_2);
        break;
      case 3:
        setCurrentHeader(HEADER_STEP_3);
        break;
      case 4:
        setCurrentHeader(HEADER_STEP_4);
        break;
      case 5:
        setCurrentHeader(HEADER_STEP_5);
        break;
      default:
        setCurrentHeader(HEADER_STEP_1);
    }
  };

  const renderScreen = (step) => {
    switch (step) {
      case 1:
        return <TripStep1 />;
      case 2:
        return <TripStep2 />;
      case 3:
        return <TripStep3 />;
      case 4:
        return <TripStep4 />;
      case 5:
        return <TripStep5 />;
      default:
        return <TripStep1 />;
    }
  };

  const handleCreate = async () => {
    const commonPayload = {
      stops,
      startDate: tripDetails.startDate,
      timeSlot: tripDetails.timeSlot,
      driverName: tripDetails.driverName,
      driverId: tripDetails.driverId,
      trikeId: tripDetails.trikeId,
      trikeRegistrationNo: tripDetails.trikeRegistrationNo,
      distance: isRouteOptimised
        ? optimisedRouteDistance
        : calculatedRouteDistance,
      time: isRouteOptimised ? optimisedRouteTime : calculatedRouteTime,
      batteries: tripDetails.batteries,
      trikeType: tripDetails?.trikeType,
      containerType: Number(tripDetails?.containerType || 0),
      canisters: Number(tripDetails?.canisters || 0),
      crates: Number(tripDetails?.crates || 0),
      estimatedRequiredEnergy: tripDetails?.estimatedRequiredEnergy || 0,
    };

    try {
      if (editTripDetails.flag) {
        const payload = { ...commonPayload, tripId: editTripDetails.tripId };
        const response = await dispatch(editTrip(payload));
        handleResponse(response, dispatch, navigate, toastMessage, "edit");
        return;
      }

      const payload = {
        ...commonPayload,
        endDate: tripDetails.endDate,
        occurence: tripDetails.occurence,
        daysOfOccurence: tripDetails.daysOfOccurence,
      };
      const response = await dispatch(createTrip(payload));
      handleResponse(response, dispatch, navigate, toastMessage, "create");
    } catch (err) {
      console.log(err, "error in creating trip");
    }
  };

  const handleNext = async () => {
    // if the tenant has no trike permission then do not show steps 4 and 5
    if (!hasTenantTrikePermission && currentStep === 3) {
      handleCreate();
      return;
    }

    // if the tenant has trike permission but does not have battery permission then do not show step 5
    if (!hasTenantBatteryPermission && currentStep === 4) {
      handleCreate();
      return;
    }

    //
    if (currentStep === 4 && tripDetails?.trikeType === "ICE") {
      handleCreate();
      return;
    }

    // if last step, create the trip
    if (currentStep === 5) {
      handleCreate();
      return;
    }

    // check for validations for step 1, 2 and 3 (step 4 and 5 are optional so no validation required)
    if (currentStep < 5) {
      const validationPassed = checkValidaton(
        currentStep,
        stops,
        dispatch,
        tripDetails,
        updateTrips,
        validationForSteps,
      );

      if (validationPassed) {
        if (currentStep === 1 && !isRouteOptimised) {
          dispatch(updateTrips({ key: "tripStep1Modal", value: true }));
        } else {
          if (currentStep === 4) {
            // console.log("current step inside 4");

            const trike = allTrikes.find(
              (item) => item.id === tripDetails?.trikeId,
            );

            console.log(trike, "check trike");
          }

          dispatch(
            updateTrips({
              key: "currentStep",
              value: currentStep + 1,
            }),
          );
        }
      }
    }
  };

  const handleCancel = () => {
    if (currentStep === 1) {
      dispatch(updateTrips({ key: "cancelTripModal", value: true }));
      return;
    }

    dispatch(
      updateTrips({
        key: "currentStep",
        value: currentStep - 1,
      }),
    );
  };

  useEffect(() => {
    changeStepHeader(currentStep);
  }, [currentStep]);

  useEffect(() => {
    dispatch(updateTrips({ key: "isRouteOptimised", value: false }));

    if (!editTripDetails.flag) {
      dispatch(resetTrips(["calculatedRouteDistance"]));
      dispatch(resetTrips(["calculatedRouteTime"]));
    }
  }, [dispatch, stops, editTripDetails]);

  return (
    <CustomLayout pageName="Trips" hideFooter>
      <div className="h-full bg-background-white">
        <div className="flex justify-between py-[13px] px-6 border-b border-light-grey">
          <StepProgress
            currentStep={currentStep}
            currentHeader={currentHeader}
          />
          <button
            type="button"
            onClick={() =>
              dispatch(updateTrips({ key: "cancelTripModal", value: true }))
            }
          >
            <CloseIcon className="w-6 h-6 stroke-dark-gray" />
          </button>
        </div>

        <div className="flex items-center justify-between pb-6 mx-6 mt-6 border-b border-light-grey">
          <StepDescription step={currentHeader} />

          <div className="flex items-center gap-x-3">
            <Button
              size="primary"
              width="generateUser"
              colors="grey"
              onClick={handleCancel}
            >
              {currentStep === 1 ? "Cancel" : "Previous"}
            </Button>

            <Button
              size="primary"
              width="generateUser"
              type="submit"
              onClick={handleNext}
            >
              {/* {currentStep === 5 && editTripDetails.flag
                ? "Save"
                : currentStep === 5
                ? "Create Trip"
                : "Next"} */}
              {getSaveButtonText({
                currentStep,
                editTripDetails,
                hasTenantTrikePermission,
                hasTenantBatteryPermission,
                tripDetails,
              })}
            </Button>
          </div>
        </div>

        <div className="w-full h-full p-6">{renderScreen(currentStep)}</div>
      </div>

      <TripStep1Modal
        visible={tripStep1Modal}
        onCancel={() =>
          dispatch(updateTrips({ key: "tripStep1Modal", value: false }))
        }
      />
      <CancelTripModal
        visible={cancelTripModal}
        onCancel={() =>
          dispatch(updateTrips({ key: "cancelTripModal", value: false }))
        }
      />
    </CustomLayout>
  );
};

export default TripScreen;
